/**
 * Les échéances d'un dossier — chunk 9.AF.1.
 *
 * Répond aux questions que le conseiller se pose sur le TEMPS : qu'est-ce qui est
 * dû bientôt, qu'est-ce qui est dépassé, qui doit s'en occuper, et a-t-on déjà
 * relancé.
 *
 * Rien n'est décidé ici. L'état temporel, le responsable et la prochaine action
 * arrivent qualifiés du serveur — les recalculer installerait un second seuil de
 * « échéance proche », et un dossier serait « urgent » sur un écran et « à venir »
 * sur l'autre.
 */
import type { DeadlineItem, DeadlineState, DeadlineSummary } from "@kollectivia/shared";
import { useTranslation } from "react-i18next";

import { formatDeadlineDate } from "../../../lib/formatDeadlineDate";
import { useProjectDeadlines } from "../../../lib/queries/deadlines";

/**
 * Le rouge est réservé au dépassement, l'ambre à l'imminence, le violet à l'absence
 * de responsable. Trois problèmes différents, trois traitements — un écran où tout
 * est rouge ne signale plus rien.
 */
const TON: Record<DeadlineState, string> = {
  overdue: "bg-red-50 text-red-800 ring-red-200",
  unassigned: "bg-violet-50 text-violet-800 ring-violet-200",
  due_soon: "bg-amber-50 text-amber-800 ring-amber-200",
  upcoming: "bg-slate-100 text-slate-700 ring-slate-200",
  resolved: "bg-emerald-50 text-emerald-800 ring-emerald-200",
};

/** Normalise une charge incomplète plutôt que d'emporter la page avec elle. */
function normaliser(brut: DeadlineSummary | undefined): DeadlineSummary | null {
  if (!brut || typeof brut !== "object") return null;
  if (typeof brut.next_action !== "string") return null;
  return {
    ...brut,
    items: Array.isArray(brut.items) ? brut.items.filter((i) => i?.id) : [],
    waiting_on: Array.isArray(brut.waiting_on) ? brut.waiting_on : [],
    overdue_count: Number.isFinite(brut.overdue_count) ? brut.overdue_count : 0,
    due_soon_count: Number.isFinite(brut.due_soon_count) ? brut.due_soon_count : 0,
    upcoming_count: Number.isFinite(brut.upcoming_count) ? brut.upcoming_count : 0,
    unassigned_count: Number.isFinite(brut.unassigned_count) ? brut.unassigned_count : 0,
  };
}

function Ligne({ item }: { readonly item: DeadlineItem }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.startsWith("en") ? "en" : "fr";

  return (
    <li className="flex flex-wrap items-start gap-x-3 gap-y-1 border-t border-slate-100 py-3 first:border-t-0">
      <span
        className={`mt-0.5 inline-flex shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${TON[item.state] ?? TON.upcoming}`}
      >
        {t(`deadlines.state.${item.state}`)}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-slate-900">{item.label}</span>
        <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
          <span>{t(`deadlines.kind.${item.kind}`)}</span>
          <span aria-hidden="true">·</span>
          <span>{t(`deadlines.waiting.${item.waiting_on}`)}</span>
          {item.due_at !== null && (
            <>
              <span aria-hidden="true">·</span>
              <span className={item.state === "overdue" ? "font-medium text-red-700" : undefined}>
                {item.days_remaining !== null && item.days_remaining < 0
                  ? t("deadlines.overdue_by", { count: Math.abs(item.days_remaining) })
                  : t("deadlines.due_on", {
                      date: formatDeadlineDate(item, lang),
                    })}
              </span>
            </>
          )}
          <span aria-hidden="true">·</span>
          {/* Un responsable désactivé n'est PAS affiché comme responsable : montrer
              son nom laisserait croire que quelqu'un s'en occupe. */}
          <span className={item.responsible_inactive ? "font-medium text-violet-700" : undefined}>
            {item.responsible_inactive
              ? t("deadlines.owner_inactive")
              : (item.responsible_name ?? t("deadlines.no_owner"))}
          </span>
          {item.last_reminder_at !== null && (
            <>
              <span aria-hidden="true">·</span>
              <span>
                {t("deadlines.last_reminder", {
                  date: new Date(item.last_reminder_at).toLocaleDateString(lang),
                })}
              </span>
            </>
          )}
        </span>
      </span>
    </li>
  );
}

export function ProjectDeadlinesSection({ projectId }: { readonly projectId: string }) {
  const { t } = useTranslation();
  const req = useProjectDeadlines(projectId);

  if (req.isPending) {
    return (
      <section className="mt-6 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <p className="text-sm text-slate-500" aria-live="polite">
          {t("deadlines.loading")}
        </p>
      </section>
    );
  }

  const d = normaliser(req.data);
  if (req.isError || d === null) {
    return (
      <section className="mt-6 rounded-xl border border-red-200 bg-red-50 p-5 shadow-sm">
        <p className="text-sm text-red-800" role="alert">
          {t("deadlines.error")}
        </p>
        <button
          type="button"
          className="btn-ghost mt-3 min-h-11"
          disabled={req.isFetching}
          onClick={() => void req.refetch()}
        >
          {t("common.retry")}
        </button>
      </section>
    );
  }

  /**
   * 9.BI.1 — un bloc qui n'a rien à dire ne s'affiche pas.
   *
   * Sur un dossier dont les assureurs ont répondu, ce bloc s'ouvrait sur son
   * titre, « Rien en attente sur ce dossier. », puis un encadré « Aucune
   * échéance sur ce dossier. » — la même absence énoncée deux fois, sous un
   * titre qui annonçait des échéances qu'il n'y avait pas.
   *
   * Dire « rien ne vous attend » reste utile : c'est le travail de
   * `ProchaineAction`, juste au-dessus, et il le fait déjà. Ce bloc-ci DÉTAILLE
   * — sans échéance ni compte à détailler, il ne reste que du mobilier.
   */
  const rienADetailler =
    d.items.length === 0 &&
    d.overdue_count === 0 &&
    d.due_soon_count === 0 &&
    d.unassigned_count === 0;
  if (rienADetailler) return null;

  return (
    <section className="mt-6 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <h2 className="text-base font-semibold text-slate-900">{t("deadlines.title")}</h2>

      <p className="mt-2 text-sm font-medium text-slate-800">
        {t(`deadlines.next_action.${d.next_action}`)}
      </p>

      {(d.overdue_count > 0 || d.due_soon_count > 0 || d.unassigned_count > 0) && (
        <p className="mt-1 flex flex-wrap gap-x-3 text-sm text-slate-600">
          {d.overdue_count > 0 && (
            <span className="font-medium text-red-700">
              {t("deadlines.count.overdue", { count: d.overdue_count })}
            </span>
          )}
          {d.due_soon_count > 0 && (
            <span>{t("deadlines.count.due_soon", { count: d.due_soon_count })}</span>
          )}
          {d.unassigned_count > 0 && (
            <span className="text-violet-700">
              {t("deadlines.count.unassigned", { count: d.unassigned_count })}
            </span>
          )}
        </p>
      )}

      {d.items.length === 0 ? (
        <p className="mt-4 rounded-lg border border-dashed border-slate-300 px-4 py-6 text-center text-sm text-slate-600">
          {t("deadlines.empty")}
        </p>
      ) : (
        <ul className="mt-4">
          {d.items.map((i) => (
            <Ligne key={i.id} item={i} />
          ))}
        </ul>
      )}
    </section>
  );
}
